import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import Reveal from "../components/Reveal";
import GoldDivider from "../components/GoldDivider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interestOptions = [
    { v: "commercial", l: "Commercial" },
    { v: "residential", l: "Luxury Residential" },
    { v: "services", l: "Administration Services" },
    { v: "general", l: "General Inquiry" },
];

const inputCls =
    "w-full bg-transparent border-b border-white/15 focus:border-brand-gold outline-none py-3 text-brand-silver placeholder:text-brand-silver/40 transition-colors";

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="text-[0.65rem] tracking-[0.35em] uppercase text-brand-silver/60">
            {label} {required && <span className="text-brand-gold">*</span>}
        </span>
        {children}
    </label>
);

const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        interest: "general",
        budget: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please complete the required fields.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, form);
            setSubmitted(true);
            toast.success("Inquiry received. A partner will respond within 24 hours.");
            setForm({ name: "", email: "", phone: "", interest: "general", budget: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="page-contact">
            <section className="pt-44 pb-16 text-center px-6">
                <Reveal>
                    <p className="text-[0.7rem] tracking-[0.55em] uppercase text-brand-gold mb-6">Private Consultation</p>
                    <h1 className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl text-brand-silver leading-[1.05]">
                        By appointment <span className="italic text-brand-champagne">only.</span>
                    </h1>
                    <p className="mt-8 text-brand-silver/75 max-w-2xl mx-auto leading-relaxed">
                        Share a few details and a partner will respond personally within twenty-four hours.
                    </p>
                </Reveal>
            </section>

            <section className="pb-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-16">
                    <Reveal className="md:col-span-7">
                        <form
                            onSubmit={handleSubmit}
                            className="bg-brand-onyx border border-brand-gold/15 p-8 md:p-12 space-y-7"
                            data-testid="contact-form"
                        >
                            {submitted && (
                                <div
                                    className="border border-brand-gold/40 bg-brand-gold/5 text-brand-champagne p-5 text-sm"
                                    data-testid="contact-form-success"
                                >
                                    Thank you. Your private inquiry has been received. A partner will be in touch within 24 hours.
                                </div>
                            )}
                            <div className="grid md:grid-cols-2 gap-7">
                                <Field label="Full Name" required>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        className={inputCls}
                                        data-testid="contact-input-name"
                                        required
                                    />
                                </Field>
                                <Field label="Email" required>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        className={inputCls}
                                        data-testid="contact-input-email"
                                        required
                                    />
                                </Field>
                                <Field label="Phone">
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        className={inputCls}
                                        data-testid="contact-input-phone"
                                    />
                                </Field>
                                <Field label="Budget Range">
                                    <input
                                        type="text"
                                        name="budget"
                                        placeholder="$5M – $20M"
                                        value={form.budget}
                                        onChange={handleChange}
                                        className={inputCls}
                                        data-testid="contact-input-budget"
                                    />
                                </Field>
                            </div>

                            <Field label="Interest">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                                    {interestOptions.map((o) => (
                                        <label
                                            key={o.v}
                                            className={`cursor-pointer border px-3 py-3 text-[0.65rem] tracking-[0.25em] uppercase text-center transition-all ${
                                                form.interest === o.v
                                                    ? "border-brand-gold text-brand-gold bg-brand-gold/5"
                                                    : "border-white/10 text-brand-silver/70 hover:border-brand-gold/50"
                                            }`}
                                            data-testid={`contact-interest-${o.v}`}
                                        >
                                            <input
                                                type="radio"
                                                name="interest"
                                                value={o.v}
                                                checked={form.interest === o.v}
                                                onChange={handleChange}
                                                className="hidden"
                                            />
                                            {o.l}
                                        </label>
                                    ))}
                                </div>
                            </Field>

                            <Field label="Message" required>
                                <textarea
                                    name="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={handleChange}
                                    className={`${inputCls} resize-none`}
                                    placeholder="Tell us about the property, mandate, or inquiry."
                                    data-testid="contact-input-message"
                                    required
                                />
                            </Field>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full md:w-auto inline-flex items-center justify-center gap-3 text-xs tracking-[0.35em] uppercase border border-brand-gold bg-brand-gold text-brand-jet px-10 py-5 hover:bg-transparent hover:text-brand-gold transition-all duration-500 disabled:opacity-60"
                                    data-testid="contact-submit-button"
                                >
                                    {loading ? "Sending..." : (<>Send Inquiry <Send size={14} /></>)}
                                </button>
                            </div>
                        </form>
                    </Reveal>

                    <Reveal delay={150} className="md:col-span-5 space-y-10">
                        <div>
                            <p className="text-[0.7rem] tracking-[0.45em] uppercase text-brand-gold mb-5">Headquarters</p>
                            <div className="space-y-5 text-sm">
                                <a
                                    href="https://maps.google.com/?q=2685+S+Rainbow+Blvd+suite+212+Las+Vegas+NV+89146"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex gap-4 items-start hover:text-brand-gold transition"
                                    data-testid="contact-address"
                                >
                                    <MapPin size={18} className="text-brand-gold shrink-0 mt-0.5" />
                                    <span className="text-brand-silver/85">
                                        2685 S Rainbow Blvd, Suite 212<br />Las Vegas, NV 89146
                                    </span>
                                </a>
                                <a href="tel:+18336494628" className="flex gap-4 items-center hover:text-brand-gold transition" data-testid="contact-phone">
                                    <Phone size={18} className="text-brand-gold shrink-0" />
                                    <span className="text-brand-silver/85">833.649.4628</span>
                                </a>
                                <a href="mailto:info@adminrealestatehq.online" className="flex gap-4 items-center hover:text-brand-gold transition" data-testid="contact-email">
                                    <Mail size={18} className="text-brand-gold shrink-0" />
                                    <span className="text-brand-silver/85">info@adminrealestatehq.online</span>
                                </a>
                                <div className="flex gap-4 items-start">
                                    <Clock size={18} className="text-brand-gold shrink-0 mt-0.5" />
                                    <span className="text-brand-silver/85">
                                        Mon – Fri · 9:00 – 18:00 PT<br />
                                        Evenings &amp; weekends by appointment
                                    </span>
                                </div>
                            </div>
                        </div>

                        <GoldDivider />

                        <div className="overflow-hidden border border-brand-gold/20">
                            <iframe
                                title="Real Estate & Administration Inc Headquarters"
                                src="https://www.google.com/maps?q=2685+S+Rainbow+Blvd+suite+212+Las+Vegas+NV+89146&output=embed"
                                width="100%"
                                height="300"
                                style={{ border: 0, filter: "grayscale(1) contrast(1.1) brightness(0.85)" }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="contact-map"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Contact;
